import { srcSetFor, baseOf, SIZES } from "../lib/img";
import React, { useEffect, useRef, useState } from "react";
import "./Home.css";
import "./Programs.css";
import "./TripDetail.css";
import "./Trips.css";
import "./DubaiWinter.css";
import TripRegistrationModal from "../components/TripRegistrationModal";
import SEO from "../components/SEO";
import SiteHeader from "../components/SiteHeader";
import ScrollStory from "../components/ScrollStory";
import { useLocale, useL } from "../i18n/LocaleContext";
import { DUBAI_WINTER_CAMP, DUBAI_WINTER_HERO } from "../data/trips";
import { breadcrumbSchema, faqPageSchema, campTripSchema } from "../data/schemas";

const ARROW = (
  <svg className="i-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

/* AED figure for the visitor's language. Arabic puts the currency word
   after the number; Russian uses a space as the thousands separator.
   See the glossary in I18N.md. */
function formatAed(amount, locale) {
  if (locale === "ar") return `${amount.toLocaleString("en-US")} درهم`;
  if (locale === "ru") return `${amount.toLocaleString("ru-RU")} AED`;
  return `AED ${amount.toLocaleString("en-US")}`;
}

/* ─────────── Nav ─────────── */

/* ScrollStory steps for the Dubai trip. desert.jpg (camel) was
   removed in round 49 for sharpness; hero.jpg (waterfront) now
   fills the freed slot and Hatta folds into the nights step's copy.
   See OWNER-TODO §22 — a proper Hatta camping photo would restore
   the original 4-distinct-per-page plan (hero.jpg currently sits on
   both the /trips card and this page's hero, and now also here).
   Naming rule holds in all three languages: no hotel/company/entity
   names anywhere beyond the owner whitelist. */
const DUBAI_STORY_STEPS = [
  {
    eyebrow: {
      en: "Every learning day",
      ar: "كل يوم تعلّم",
      ru: "Каждый учебный день",
    },
    title: {
      en: "Mornings train the mind, days go inside real institutions",
      ar: "الصباح يدرّب العقل، والنهار داخل مؤسسات حقيقية",
      ru: "Утро тренирует мышление, день проходит внутри реальных институтов",
    },
    body: {
      en: "Mornings begin with two hours of Kidsology classes in character development, leadership, communication, English, AI, and business. Days are for workshops and visits inside major UAE institutions, global companies, and universities, like the American University in Dubai and Al Jalila Cultural Centre, with more big names shared with registered families first.",
      ar: "تبدأ الصباحات بساعتين من حصص Kidsology في بناء الشخصية والقيادة والتواصل والإنجليزية والذكاء الاصطناعي والأعمال. أما النهار فللورش والزيارات داخل مؤسسات إماراتية كبرى وشركات عالمية وجامعات، مثل الجامعة الأمريكية في دبي ومركز الجليلة الثقافي، مع أسماء كبيرة أخرى تُشارك مع الأسر المسجلة أولاً.",
      ru: "Утро начинается с двух часов занятий Kidsology по развитию характера, лидерству, коммуникации, английскому, ИИ и бизнесу. Днём идут воркшопы и визиты внутрь крупных учреждений ОАЭ, международных компаний и университетов, таких как Американский университет в Дубае и Культурный центр Al Jalila, а другие большие имена мы сначала раскрываем зарегистрированным семьям.",
    },
    chips: [
      { en: "9 days, 8 nights", ar: "9 أيام و8 ليالٍ", ru: "9 дней и 8 ночей" },
      {
        en: "Kidsology supervision",
        ar: "إشراف Kidsology",
        ru: "Присмотр Kidsology",
      },
    ],
    image: "/images/trips/dubai/classes.jpg",
    imageAlt: {
      en: "Iconic Dubai landmark visited during the camp",
      ar: "أحد معالم دبي الشهيرة التي تُزار خلال المخيم",
      ru: "Известная достопримечательность Дубая, которую посещают во время лагеря",
    },
  },
  {
    eyebrow: { en: "Days", ar: "الأيام", ru: "Дни" },
    title: {
      en: "A new skill every day",
      ar: "مهارة جديدة كل يوم",
      ru: "Каждый день новый навык",
    },
    body: {
      en: "English. AI. Business. Communication. Leadership. Character development. Horse riding. A new skill every day.",
      ar: "الإنجليزية. الذكاء الاصطناعي. الأعمال. التواصل. القيادة. بناء الشخصية. الفروسية. مهارة جديدة كل يوم.",
      ru: "Английский. ИИ. Бизнес. Коммуникация. Лидерство. Развитие характера. Верховая езда. Каждый день новый навык.",
    },
    chips: [
      { en: "English", ar: "الإنجليزية", ru: "Английский" },
      { en: "AI", ar: "الذكاء الاصطناعي", ru: "ИИ" },
      { en: "Business", ar: "الأعمال", ru: "Бизнес" },
      { en: "Horse riding", ar: "الفروسية", ru: "Верховая езда" },
    ],
    image: "/images/trips/dubai/skill.jpg",
    imageAlt: {
      en: "Children active outdoors at a beachfront hotel",
      ar: "أطفال ينشطون في الهواء الطلق في فندق على الشاطئ",
      ru: "Дети занимаются на воздухе у отеля на берегу",
    },
  },
  {
    eyebrow: { en: "Between activities", ar: "بين الأنشطة", ru: "Между занятиями" },
    title: {
      en: "The city, one week home",
      ar: "المدينة، بيت لأسبوع",
      ru: "Город, который на неделю становится домом",
    },
    body: {
      en: "Nine days in Dubai turn the city into a base. Walks along the water at golden hour, city landmarks between activities, a rhythm that feels like living here.",
      ar: "تسعة أيام في دبي تجعل المدينة قاعدة للإقامة. مشي على الواجهة المائية في الساعة الذهبية، ومعالم المدينة بين الأنشطة، وإيقاع يشبه الحياة هنا.",
      ru: "Девять дней в Дубае превращают город в базу. Прогулки у воды в золотой час, городские виды между занятиями, ритм, в котором чувствуешь себя местным.",
    },
    chips: [
      { en: "Dubai as a base", ar: "دبي قاعدة الإقامة", ru: "Дубай как база" },
      { en: "9 days, 8 nights", ar: "9 أيام و8 ليالٍ", ru: "9 дней и 8 ночей" },
      {
        en: "One city away from home",
        ar: "مدينة واحدة عن البيت",
        ru: "Один город от дома",
      },
    ],
    image: "/images/trips/dubai/city.jpg",
    imageAlt: {
      en: "Aerial view of the Dubai coastline at golden hour",
      ar: "منظر جوي لساحل دبي في الساعة الذهبية",
      ru: "Вид с воздуха на берег Дубая в золотой час",
    },
  },
  {
    eyebrow: {
      en: "Adventure and nights",
      ar: "المغامرة والليل",
      ru: "Приключения и вечера",
    },
    title: {
      en: "Adventure by day, Dubai at night",
      ar: "مغامرة في النهار، ودبي في الليل",
      ru: "Днём приключения, вечером Дубай",
    },
    body: {
      en: "Two days are pure adventure. In Hatta, children set up their own tents and learn what real camping takes. And one full day is just play, water and sun and being a kid. Nights are Dubai itself: the desert, Global Village, Dubai Safari, the beaches. The city becomes the classroom.",
      ar: "يومان مغامرة خالصة. في حتا ينصب الأطفال خيامهم بأنفسهم ويتعلمون ما يتطلبه التخييم الحقيقي. ويوم كامل للعب فقط، ماء وشمس وأن تكون طفلاً. والليل لدبي نفسها: الصحراء، القرية العالمية، دبي سفاري، الشواطئ. تصبح المدينة هي الصف.",
      ru: "Два полных дня отданы приключениям. В Хатте дети сами ставят палатки и узнают, чего требует настоящий кемпинг. А ещё один полный день отдан игре, воде, солнцу и тому, чтобы просто быть ребёнком. Вечера отданы самому Дубаю: пустыня, Global Village, Dubai Safari, пляжи. Город становится классом.",
    },
    chips: [
      { en: "Adventure days", ar: "أيام المغامرة", ru: "Дни приключений" },
      { en: "Global Village", ar: "القرية العالمية", ru: "Global Village" },
      { en: "Dubai Safari", ar: "دبي سفاري", ru: "Dubai Safari" },
      { en: "The beaches", ar: "الشواطئ", ru: "Пляжи" },
    ],
    image: "/images/trips/dubai/night.jpg",
    imageAlt: {
      en: "Dubai city skyline at dusk",
      ar: "أفق مدينة دبي عند الغروب",
      ru: "Панорама Дубая в сумерках",
    },
  },
];

/* Five FAQ entries per spec — visit-schedule answer notes the full
   schedule goes to registered families first. */
const FAQS = [
  {
    q: {
      en: "What does a day look like?",
      ar: "كيف يبدو اليوم؟",
      ru: "Как выглядит день?",
    },
    a: {
      en: "Days come in three parts. Mornings are two hours of Kidsology classes in character and life skills. The middle of the day is either learning a real skill or going inside a major UAE institution, company, or university. Nights are Dubai itself: the desert, Global Village, Dubai Safari, the beaches.",
      ar: "اليوم من ثلاثة أجزاء. الصباح ساعتان من حصص Kidsology في الشخصية ومهارات الحياة. ووسط النهار إما تعلّم مهارة حقيقية أو زيارة داخل مؤسسة إماراتية كبرى أو شركة أو جامعة. والليل لدبي نفسها: الصحراء، القرية العالمية، دبي سفاري، الشواطئ.",
      ru: "День состоит из трёх частей. Утром идут два часа занятий Kidsology по характеру и жизненным навыкам. В середине дня либо освоение реального навыка, либо визит внутрь крупного учреждения ОАЭ, компании или университета. Вечера отданы самому Дубаю: пустыня, Global Village, Dubai Safari, пляжи.",
    },
  },
  {
    q: {
      en: "Where do children sleep?",
      ar: "أين ينام الأطفال؟",
      ru: "Где дети живут?",
    },
    a: {
      en: "In a hotel in Dubai, for eight nights. Kidsology supervision is on site day and night. For many children this is their first residential experience away from home, and the setup is built around that.",
      ar: "في فندق في دبي، لمدة ثماني ليالٍ. وإشراف Kidsology موجود في الموقع ليلاً ونهاراً. هذه أول تجربة إقامة بعيداً عن البيت لكثير من الأطفال، والترتيب كله مبني على ذلك.",
      ru: "В отеле в Дубае, восемь ночей. Команда Kidsology находится на месте днём и ночью. Для многих детей это первый выезд с проживанием без семьи, и весь формат построен вокруг этого.",
    },
  },
  {
    q: {
      en: "How safe is the camp?",
      ar: "ما مدى أمان المخيم؟",
      ru: "Насколько лагерь безопасен?",
    },
    a: {
      en: "Children stay together with the Kidsology team, supervised day and night. Parents are contacted before registration is confirmed, and our team stays reachable throughout the camp.",
      ar: "يبقى الأطفال معاً مع فريق Kidsology، بإشراف ليلاً ونهاراً. ويتم التواصل مع الأهل قبل تأكيد التسجيل، ويبقى فريقنا متاحاً خلال المخيم.",
      ru: "Дети живут вместе с командой Kidsology, под присмотром днём и ночью. С родителями связываются до подтверждения регистрации, и наша команда остаётся на связи всё время лагеря.",
    },
  },
  {
    q: {
      en: "What ages is this for?",
      ar: "لأي أعمار هذا المخيم؟",
      ru: "Для какого возраста лагерь?",
    },
    a: {
      en: "Children aged 5 to 18, grouped by age. Groups are set so activities and supervision fit each stage.",
      ar: "للأطفال من 5 إلى 18 عاماً، مقسّمين حسب العمر. وتُشكّل المجموعات بحيث تناسب الأنشطة والإشراف كل مرحلة.",
      ru: "Для детей от 5 до 18 лет, с делением по возрасту. Группы формируют так, чтобы занятия и присмотр подходили каждому этапу.",
    },
  },
  {
    q: {
      en: "What is included?",
      ar: "ماذا يشمل السعر؟",
      ru: "Что входит в программу?",
    },
    a: {
      en: "Nine days and eight nights of accommodation, all meals, every Kidsology class, every learning day and adventure day, all city visits, and full Kidsology supervision. The full visit schedule is shared with registered families first.",
      ar: "تسعة أيام وثماني ليالٍ من الإقامة، وكل الوجبات، وكل حصص Kidsology، وكل أيام التعلّم والمغامرة، وكل زيارات المدينة، وإشراف Kidsology الكامل. ويُشارك جدول الزيارات الكامل مع الأسر المسجلة أولاً.",
      ru: "Девять дней и восемь ночей проживания, всё питание, все занятия Kidsology, все учебные дни и дни приключений, все городские визиты и полный присмотр Kidsology. Полное расписание визитов мы сначала присылаем зарегистрированным семьям.",
    },
  },
  {
    q: {
      en: "What is the cancellation policy?",
      ar: "ما هي سياسة الإلغاء؟",
      ru: "Какие условия отмены?",
    },
    a: [
      {
        en: "If you choose to cancel your booking, 50% of the camp fee is refunded. Visa application fees are non refundable and are not part of the cancellation policy.",
        ar: "إذا اخترت إلغاء الحجز، يُرد 50% من رسوم المخيم. ورسوم طلب التأشيرة غير قابلة للاسترداد وليست جزءاً من سياسة الإلغاء.",
        ru: "Если вы отменяете бронирование, возвращается 50% стоимости лагеря. Сборы за визовое заявление не возвращаются и не входят в условия отмены.",
      },
      {
        en: "If any visa application is refused, all camp fees are refunded minus the airline ticket cancellation fee, subject to the airline's policy. Visa fees remain non refundable.",
        ar: "وإذا رُفض أي طلب تأشيرة، تُرد كل رسوم المخيم مع خصم رسم إلغاء تذكرة الطيران، وفق سياسة شركة الطيران. وتبقى رسوم التأشيرة غير قابلة للاسترداد.",
        ru: "Если визовое заявление отклонено, все сборы за лагерь возвращаются за вычетом сбора за отмену авиабилета, по правилам авиакомпании. Визовые сборы остаются невозвратными.",
      },
      {
        en: "If the camp or flights cannot operate due to extraordinary circumstances, such as war or airport closures, all camp fees are fully refunded. The only deduction is the airline's administrative cancellation charge, confirmed by our travel agent at the time of cancellation. Where applicable, the programme's travel insurance also covers eligible costs through the airline or the insurance provider.",
        ar: "وإذا تعذّر تنفيذ المخيم أو الرحلات الجوية لأسباب استثنائية، مثل الحرب أو إغلاق المطارات، تُرد كل رسوم المخيم بالكامل. والخصم الوحيد هو الرسم الإداري لإلغاء التذكرة من شركة الطيران، ويؤكده وكيل السفر عند الإلغاء. وحيث ينطبق ذلك، يغطي تأمين السفر الخاص بالبرنامج التكاليف المستحقة عبر شركة الطيران أو شركة التأمين.",
        ru: "Если лагерь или перелёты невозможны из-за исключительных обстоятельств, таких как война или закрытие аэропортов, все сборы за лагерь возвращаются полностью. Удерживается только административный сбор авиакомпании за отмену, который подтверждает наш турагент в момент отмены. Там, где это применимо, страховка программы также покрывает соответствующие расходы через авиакомпанию или страховую компанию.",
      },
      {
        en: "If a return flight is disrupted, participants are accommodated in a hotel until an alternative flight is arranged, with accommodation costs covered by the airline or the insurance provider where applicable.",
        ar: "وإذا تعطّلت رحلة العودة، يُسكن المشاركون في فندق حتى يتم تأمين رحلة بديلة، وتتحمل شركة الطيران أو شركة التأمين تكاليف الإقامة حيث ينطبق ذلك.",
        ru: "Если обратный рейс нарушен, участников размещают в отеле до организации альтернативного рейса, а расходы на проживание покрывает авиакомпания или страховая компания, где это применимо.",
      },
    ],
  },
  {
    q: {
      en: "How do I register?",
      ar: "كيف أسجّل؟",
      ru: "Как зарегистрироваться?",
    },
    a: {
      en: "Press Register Now. We will contact you to walk you through the details and confirm a place. No payment is required at this stage.",
      ar: "اضغط على «سجّل الآن». سنتواصل معك لنشرح التفاصيل ونثبّت المكان. ولا حاجة لأي دفعة في هذه المرحلة.",
      ru: "Нажмите «Зарегистрироваться». Мы свяжемся с вами, расскажем детали и закрепим место. Оплата на этом этапе не требуется.",
    },
  },
];

function DubaiFAQ() {
  const { t } = useLocale();
  const faqs = useL(FAQS);
  return (
    <section className="td-faq td-section" aria-labelledby="dw-faq-title">
      <div className="td-section-inner">
        <span className="td-eyebrow">
          {t({ en: "Questions", ar: "أسئلة", ru: "Вопросы" })}
        </span>
        <h2 id="dw-faq-title" className="td-h2 pg-display">
          {t({
            en: "Everything a parent asks first.",
            ar: "كل ما يسأله الأهل أولاً.",
            ru: "Всё, о чём родители спрашивают первым.",
          })}
        </h2>
        <div className="td-faq-list">
          {faqs.map((f, fi) => (
            <details className="td-faq-item" key={fi}>
              <summary>
                <span>{f.q}</span>
                <span className="td-faq-chev" aria-hidden="true">▾</span>
              </summary>
              {Array.isArray(f.a)
                ? f.a.map((para, i) => <p key={i}>{para}</p>)
                : <p>{f.a}</p>}
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────── Page ─────────── */
/* The page description, also the TouristTrip description in JSON-LD. */
const DUBAI_SEO_DESCRIPTION = {
          en: "Nine days and eight nights in Dubai for ages 5 to 18: English, AI, business, character and leadership, plus workshops inside major UAE institutions.",
          ar: "تسعة أيام وثماني ليالٍ في دبي للأعمار من 5 إلى 18 عاماً: الإنجليزية والذكاء الاصطناعي والأعمال والشخصية والقيادة، وورش داخل مؤسسات إماراتية كبرى.",
          ru: "Девять дней и восемь ночей в Дубае для детей от 5 до 18 лет: английский, ИИ, бизнес, характер и лидерство, воркшопы в крупных учреждениях ОАЭ.",
        };

export default function DubaiWinter() {
  const { t, locale } = useLocale();
  const hero = useL(DUBAI_WINTER_HERO);
  const storySteps = useL(DUBAI_STORY_STEPS);
  const faqs = useL(FAQS);
  const rootRef = useRef(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const els = rootRef.current
      ? Array.from(rootRef.current.querySelectorAll(".reveal"))
      : [];
    if (!("IntersectionObserver" in window) || els.length === 0) {
      els.forEach((el) => el.classList.add("in"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.14, rootMargin: "0px 0px -8% 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const orig = document.body.style.background;
    document.body.style.background = "var(--navy-deep)";
    return () => {
      document.body.style.background = orig;
    };
  }, []);

  /* JSON-LD. The FAQ schema is built from the same resolved list the
     page renders, so the markup always matches the visible language
     (a Google requirement). */
  const schemas = [
    /* Event schema deliberately omitted: schema.org Event requires a
       startDate, and this camp's dates are still unannounced (every
       visible string says "dates to be announced"). Re-add
       dubaiTripEventSchema(locale) once the real dates ship. */
    campTripSchema(
      {
        path: "/trips/dubai-winter",
        name: t({ en: "Dubai Winter Development Camp", ar: "مخيم دبي الشتوي للتطوير", ru: "Зимний развивающий лагерь в Дубае" }),
        description: t(DUBAI_SEO_DESCRIPTION),
        image: "/images/trips/dubai/hero.jpg",
      },
      locale,
    ),
    faqPageSchema(faqs, locale),
    breadcrumbSchema(
      [
        { name: t({ en: "Home", ar: "الرئيسية", ru: "Главная" }), path: "/" },
        { name: t({ en: "Trips", ar: "الرحلات", ru: "Поездки" }), path: "/trips" },
        {
          name: t({
            en: "Dubai Winter Development Camp",
            ar: "مخيم دبي الشتوي للتطوير",
            ru: "Зимний развивающий лагерь в Дубае",
          }),
          path: "/trips/dubai-winter",
        },
      ],
      locale
    ),
  ];

  return (
    <div className="prog-page trip-detail dubai-winter" ref={rootRef}>
      <SEO
        title={t({
          en: "Winter Camp in Dubai for Kids | Kidsology",
          ar: "مخيم شتوي في دبي للأطفال | Kidsology",
          ru: "Зимний лагерь в Дубае для детей | Kidsology",
        })}
        description={t(DUBAI_SEO_DESCRIPTION)}
        path="/trips/dubai-winter"
        image="/images/trips/dubai/hero.jpg"
        schema={schemas}
      />

      <SiteHeader />
      <main id="main">

      {/* ── HERO ── */}
      <section className="td-hero dw-hero">
        <div className="td-hero-bg">
          <picture>
            <source srcSet={srcSetFor(baseOf(hero.photo))} sizes={SIZES.full} type="image/webp" />
            <img
              src={hero.photo}
              alt={hero.photoAlt}
              width="1600"
              height="1066"
              fetchPriority="high"
            />
          </picture>
          <div className="td-hero-overlay" aria-hidden="true" />
        </div>
        <div className="td-hero-inner">
          <span className="tr-dubai-hero-badge dw-hero-badge">
            {hero.badge}
          </span>
          <h1 className="td-hero-title pg-display">
            {hero.title}
          </h1>
          <p className="dw-hero-lead">{hero.lead}</p>
          <p className="td-hero-prose">{hero.body}</p>
          {hero.priceAed && (
            <p className="dw-hero-price">
              <span className="dw-hero-price-amount">
                <bdi>{formatAed(hero.priceAed, locale)}</bdi>
              </span>
              {hero.priceNote && (
                <span className="dw-hero-price-note"> · {hero.priceNote}</span>
              )}
            </p>
          )}
          <div className="td-hero-actions">
            <button
              type="button"
              className="btn btn-gold-lg"
              onClick={() => setModalOpen(true)}
            >
              {t({ en: "Register Now", ar: "سجّل الآن", ru: "Зарегистрироваться" })} {ARROW}
            </button>
            <a
              href="https://wa.me/971528360206"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline-light"
            >
              {t({
                en: "WhatsApp the team",
                ar: "راسلنا على واتساب",
                ru: "Написать в WhatsApp",
              })}
            </a>
          </div>
        </div>
      </section>

      {/* ── SCROLL STORY: how the nine days unfold ── */}
      <ScrollStory
        kicker={t({ en: "Nine days", ar: "تسعة أيام", ru: "Девять дней" })}
        title={t({
          en: "How the days unfold.",
          ar: "كيف تمضي الأيام.",
          ru: "Как проходят дни.",
        })}
        steps={storySteps}
        mediaSide="right"
      />

      {/* ── WHERE THEY STAY (preserved below the story) ── */}
      <section className="td-section dw-stay">
        <div className="td-section-inner">
          <span className="td-eyebrow">
            {t({ en: "Where they stay", ar: "أين يقيمون", ru: "Где они живут" })}
          </span>
          <h2 className="td-h2 pg-display">
            {t({
              en: "One city away from you.",
              ar: "مدينة واحدة تفصلك عنهم.",
              ru: "В одном городе от вас.",
            })}
          </h2>
          <p className="dw-longform">
            {t({
              en: "Nine days and eight nights, with full Kidsology supervision day and night. Our team lives with the group, from the first morning to the last. For many children this is their first time staying with a group away from home, and that is exactly the point. All the independence of a residential trip, one city away from you.",
              ar: "تسعة أيام وثماني ليالٍ، بإشراف Kidsology الكامل ليلاً ونهاراً. يعيش فريقنا مع المجموعة من أول صباح إلى آخره. هذه أول مرة يقيم فيها كثير من الأطفال مع مجموعة بعيداً عن البيت، وهذا هو المقصود بالضبط. كل استقلالية الرحلة السكنية، ومدينة واحدة تفصلك عنهم.",
              ru: "Девять дней и восемь ночей под полным присмотром Kidsology днём и ночью. Наша команда живёт с группой с первого утра до последнего. Для многих детей это первый опыт жизни в группе без семьи, и в этом весь смысл. Вся самостоятельность выездной поездки, при этом всего один город от вас.",
            })}
          </p>
          <p className="dw-longform dw-insider">
            {hero.insiderLine}
          </p>
        </div>
      </section>

      {/* ── WHO IT IS FOR ── */}
      <section className="td-section dw-who">
        <div className="td-section-inner">
          <span className="td-eyebrow">
            {t({ en: "Who it is for", ar: "لمن هذا المخيم", ru: "Для кого это" })}
          </span>
          <h2 className="td-h2 pg-display">
            {t({
              en: "Ready for nine days of real.",
              ar: "مستعدون لتسعة أيام حقيقية.",
              ru: "Готовы к девяти настоящим дням.",
            })}
          </h2>
          <p className="dw-longform">
            {t({
              en: "Children aged 5 to 18 who are ready for nine days of real skills, real independence, and the best of Dubai.",
              ar: "الأطفال من 5 إلى 18 عاماً المستعدون لتسعة أيام من المهارات الحقيقية والاستقلالية الحقيقية وأفضل ما في دبي.",
              ru: "Дети от 5 до 18 лет, которые готовы к девяти дням настоящих навыков, настоящей самостоятельности и лучшего в Дубае.",
            })}
          </p>
        </div>
      </section>

      {/* ── FAQ ── */}
      <DubaiFAQ />

      {/* ── CTA STRIP ── */}
      <section className="td-section dw-cta-strip">
        <div className="td-section-inner dw-cta-inner">
          <span className="td-eyebrow">
            {t({ en: "Home, changed", ar: "يعودون مختلفين", ru: "Домой другими" })}
          </span>
          <h2 className="td-h2 pg-display">
            {t({
              en: "Nine days later.",
              ar: "بعد تسعة أيام.",
              ru: "Через девять дней.",
            })}
          </h2>
          <p className="dw-longform">
            {t({
              en: "They come back more independent, more confident, and full of stories.",
              ar: "يعودون أكثر استقلالية وأكثر ثقة ومليئين بالحكايات.",
              ru: "Они возвращаются более самостоятельными, более уверенными и полными историй.",
            })}
          </p>
          <p className="dw-longform">{hero.reassurance}</p>
          <div className="td-hero-actions">
            <button
              type="button"
              className="btn btn-gold-lg"
              onClick={() => setModalOpen(true)}
            >
              {t({ en: "Register Now", ar: "سجّل الآن", ru: "Зарегистрироваться" })} {ARROW}
            </button>
            <a
              href="https://wa.me/971528360206"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline-light"
            >
              {t({
                en: "WhatsApp the team",
                ar: "راسلنا على واتساب",
                ru: "Написать в WhatsApp",
              })}
            </a>
          </div>
        </div>
      </section>

      </main>

      {/* The modal receives the RAW trip record: it resolves display
          copy itself and writes English-only values to Firestore. */}
      <TripRegistrationModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        trip={DUBAI_WINTER_CAMP}
        mode="earlyAccess"
      />
    </div>
  );
}
